import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import Footer from '../components/Footer';
import { useAuth } from '../App';

function Utilisateurs() {
  const { user } = useAuth();
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nom: '',
    prenom: '',
    password: '',
    role: 'user',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France'
  });

  const roles = [
    { value: 'admin', label: 'Administrateur' },
    { value: 'user', label: 'Utilisateur' }
  ];

  const fetchUtilisateurs = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      setUtilisateurs(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      showSnackbar('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUtilisateurs();
  }, [fetchUtilisateurs]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        nom: user.nom || '',
        prenom: user.prenom || '',
        password: '',
        role: user.role || 'user',
        adresse: user.adresse || '',
        code_postal: user.code_postal || '',
        ville: user.ville || '',
        pays: user.pays || 'Belgique'
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        nom: '',
        prenom: '',
        password: '',
        role: 'user',
        adresse: '',
        code_postal: '',
        ville: '',
        pays: 'Belgique'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      nom: '',
      prenom: '',
      password: '',
      role: 'user',
      adresse: '',
      code_postal: '',
      ville: '',
      pays: 'Belgique'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.email || !formData.nom || !formData.prenom) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'warning');
      return;
    }

    try {
      if (editingUser) {
        // Mise à jour
        await axios.put(`/api/users/${editingUser.id}`, formData);
        showSnackbar('Utilisateur mis à jour avec succès', 'success');
      } else {
        // Création
        await axios.post('/api/users', formData);
        showSnackbar('Utilisateur créé avec succès. Un email a été envoyé pour définir le mot de passe.', 'success');
      }
      
      handleCloseDialog();
      fetchUtilisateurs();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Gestion d'erreur améliorée
      let errorMessage = 'Erreur lors de la sauvegarde';
      
      if (error.response) {
        // Erreur de réponse du serveur
        if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 400) {
          errorMessage = 'Données invalides. Vérifiez les informations saisies.';
        } else if (error.response.status === 403) {
          errorMessage = 'Accès refusé. Vous devez être administrateur.';
        } else if (error.response.status === 500) {
          errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
        }
      } else if (error.request) {
        // Erreur de connexion
        errorMessage = 'Erreur de connexion au serveur. Vérifiez votre connexion.';
      }
      
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      await axios.delete(`/api/users/${userId}`);
      showSnackbar('Utilisateur supprimé avec succès', 'success');
      fetchUtilisateurs();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showSnackbar(error.response?.data?.error || 'Erreur lors de la suppression', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Gestion des utilisateurs</Typography>
        {/* Bouton Nouvel utilisateur - Réservé aux superadmins */}
        {user?.role === 'superadmin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouvel utilisateur
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', flexGrow: 1 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nom complet</TableCell>
                <TableCell>Nom d'utilisateur</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Statut email</TableCell>
                <TableCell>Date création</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {utilisateurs.map((utilisateur) => (
                <TableRow key={utilisateur.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {utilisateur.prenom} {utilisateur.nom}
                    </Typography>
                  </TableCell>
                  <TableCell>{utilisateur.username}</TableCell>
                  <TableCell>{utilisateur.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={utilisateur.role === 'admin' ? 'Administrateur' : (utilisateur.role === 'superadmin' ? 'Super Admin' : 'Utilisateur')}
                      color={utilisateur.role === 'admin' ? 'primary' : (utilisateur.role === 'superadmin' ? 'secondary' : 'default')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={utilisateur.email_verified ? 'Vérifié' : 'Non vérifié'}
                      color={utilisateur.email_verified ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(utilisateur.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="center">
                    {/* Actions réservées au superadmin connecté */}
                    {user?.role === 'superadmin' && (
                      <>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(utilisateur)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(utilisateur.id)}
                            disabled={utilisateur.role === 'admin' && utilisateur.username === 'admin'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {/* Message pour les admins normaux */}
                    {user?.role !== 'superadmin' && (
                      <Typography variant="caption" color="text.secondary">
                        Réservé au Super Admin
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog pour créer/modifier un utilisateur */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                name="nom"
                label="Nom *"
                value={formData.nom}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="prenom"
                label="Prénom *"
                value={formData.prenom}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="username"
                label="Nom d'utilisateur *"
                value={formData.username}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="email"
                label="Email *"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="password"
                label={editingUser ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe (optionnel - sera défini par email)'}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Rôle *</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  label="Rôle *"
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                name="adresse"
                label="Adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                name="code_postal"
                label="Code postal"
                value={formData.code_postal}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="ville"
                label="Ville"
                value={formData.ville}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="pays"
                label="Pays"
                value={formData.pays}
                onChange={handleInputChange}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editingUser ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default Utilisateurs; 